// FIX-PUSH-DIAG: "בדיקת התראה של דיווח באג" - the server's trace of every gate of the real bug-report notice, in words:
// one line per step (ok / problem / note) and what to do when a step failed. Pure (gets `t`), so it is unit-tested.

type T = (key: string, opts?: Record<string, unknown>) => string;

type Source = 'mandatory' | 'event' | 'wildcard' | 'default';

export interface BugReportPushTrace {
  user: { id: string; name: string | null; email: string | null };
  recipients: {
    mode: 'chosen' | 'fallback_admins';
    configured_count: number;
    list: { id: string; name: string | null; email: string | null }[];
    in_list: boolean;
  };
  channels: { category: string | null; in_app: { enabled: boolean; source: Source }; push: { enabled: boolean; source: Source } };
  extension: { event_shown_by_extension: boolean; extension_connected: boolean; skip: boolean };
  vapid: { ready: boolean; missing: string[] };
  push_enabled: boolean;
  devices: { count: number; this_device_registered: boolean | null };
  send: {
    attempted: boolean;
    skipped: 'vapid_missing' | 'switched_off' | 'no_device' | 'channel_off' | 'extension' | null;
    sent: number;
    results: { ok: boolean; status_code: number | null; message: string | null; this_device: boolean; removed: boolean }[];
  };
  ok: boolean;
}

export type TraceStepState = 'ok' | 'fail' | 'info';

export interface TraceStep {
  key: string;
  state: TraceStepState;
  text: string;
  /** What to do - only on a failed step. */
  fix?: string;
}

export function bugReportPushTraceSteps(tr: BugReportPushTrace | null | undefined, t: T): { headline: string; ok: boolean; steps: TraceStep[] } {
  if (!tr) return { headline: t('pushDiag.failed'), ok: false, steps: [] };
  const steps: TraceStep[] = [];
  const names = tr.recipients.list.map((u) => (u.name || u.email || u.id) + (u.email && u.name ? ` (${u.email})` : '')).join(', ') || t('pushDiag.nobody');

  // (a) the recipients
  if (tr.recipients.mode === 'chosen') {
    steps.push(tr.recipients.in_list
      ? { key: 'recipients', state: 'ok', text: t('pushDiag.inChosenList', { names }) }
      : { key: 'recipients', state: 'fail', text: t('pushDiag.notInChosenList', { names }), fix: t('pushDiag.fixRecipients') });
  } else {
    steps.push(tr.recipients.in_list
      ? { key: 'recipients', state: 'ok', text: t('pushDiag.inFallback', { names }) }
      : { key: 'recipients', state: 'fail', text: t('pushDiag.notInFallback', { names }), fix: t('pushDiag.fixRecipients') });
  }
  steps.push({ key: 'reporter', state: 'info', text: t('pushDiag.reporterExcluded') });

  // (b) the preferences
  const src = (s: Source) => t(`pushDiag.source_${s}`);
  steps.push(tr.channels.push.enabled
    ? { key: 'pref_push', state: 'ok', text: t('pushDiag.prefPushOn', { source: src(tr.channels.push.source) }) }
    : { key: 'pref_push', state: 'fail', text: t('pushDiag.prefPushOff', { source: src(tr.channels.push.source) }), fix: t('pushDiag.fixPref') });
  steps.push({
    key: 'pref_in_app', state: 'info',
    text: t(tr.channels.in_app.enabled ? 'pushDiag.prefBellOn' : 'pushDiag.prefBellOff', { source: src(tr.channels.in_app.source) }),
  });

  // (c) the extension
  steps.push(tr.extension.skip
    ? { key: 'extension', state: 'fail', text: t('pushDiag.extensionSkip'), fix: t('pushDiag.fixExtension') }
    : { key: 'extension', state: 'ok', text: t('pushDiag.extensionNoSkip') });

  // (d) VAPID + (e) the manager's switch
  steps.push(tr.vapid.ready
    ? { key: 'vapid', state: 'ok', text: t('pushDiag.vapidReady') }
    : { key: 'vapid', state: 'fail', text: t('pushDiag.vapidMissing'), fix: t('pushDiag.fixEnableAll') });
  steps.push(tr.push_enabled
    ? { key: 'switch', state: 'ok', text: t('pushDiag.switchOn') }
    : { key: 'switch', state: 'fail', text: t('pushDiag.switchOff'), fix: t('pushDiag.fixEnableAll') });

  // (f) the devices
  if (tr.devices.count === 0) {
    steps.push({ key: 'devices', state: 'fail', text: t('pushDiag.noDevice'), fix: t('pushDiag.fixDevice') });
  } else {
    steps.push({ key: 'devices', state: 'ok', text: t('pushDiag.devices', { n: tr.devices.count }) });
    if (tr.devices.this_device_registered === false) {
      steps.push({ key: 'this_device', state: 'fail', text: t('pushDiag.thisDeviceNotRegistered'), fix: t('pushDiag.fixDevice') });
    } else if (tr.devices.this_device_registered === true) {
      steps.push({ key: 'this_device', state: 'ok', text: t('pushDiag.thisDeviceRegistered') });
    }
  }

  // (g) the real send
  if (!tr.send.attempted || tr.send.skipped) {
    steps.push({ key: 'send', state: 'fail', text: t(`pushDiag.notSent_${tr.send.skipped ?? 'channel_off'}`) });
  } else {
    tr.send.results.forEach((r, i) => {
      const who = r.this_device ? t('push.testThisDevice') : t('pushDiag.deviceN', { n: i + 1 });
      if (r.ok) {
        steps.push({ key: `send_${i}`, state: 'ok', text: `${who}: ${t('pushDiag.deviceAccepted')}` });
      } else {
        const why = r.status_code === 403 || r.status_code === 401 ? t('push.testWhy403')
          : r.removed ? t('push.testWhyGone')
            : r.status_code === null ? t('push.testWhyNetwork') : (r.message || '');
        steps.push({
          key: `send_${i}`, state: 'fail',
          text: `${who}: ${t('push.testRefused', { code: r.status_code ?? '?' })}${why ? ` ${why}` : ''}`,
          fix: t('pushDiag.fixDevice'),
        });
      }
    });
  }

  const ok = tr.ok && !steps.some((s) => s.state === 'fail');
  return { headline: ok ? t('pushDiag.allOk') : t('pushDiag.problem'), ok, steps };
}
